import React from 'react';
import PropTypes from 'prop-types';
import { BlockTitle } from 'framework7-react';

import { I18n } from 'Locales';
import { RowView } from 'Containers';
import { ArrowRightIn } from 'Icons';
import { mockFunction } from 'Helpers';
import { Button, Card } from 'Components';
import { ADMIN_ROLES, REGIONS, TABS } from 'Constants';

const handleCheckIn = ({
  checkInObject,
  checkOut,
  getPublishedFlowsOnCheckIn,
  isCheckedIn,
  selectedCampaignId,
  updateTransientProps,
  user = {}
}) => {
  updateTransientProps({ loadingCheckIn: selectedCampaignId });

  if (!user.tc_accepted) {
    updateTransientProps({ tc_modal: true });
    return;
  } else if (isCheckedIn) {
    checkOut(checkInObject.id);
    updateTransientProps({ processing: false, loadingCheckIn: false, checkInIdLoading: '' });
    return;
  } else if (user?.mandatory_location) {
    const proceed = () => {
      getPublishedFlowsOnCheckIn();
    };

    const deny = () => {
      updateTransientProps({ showDeniedLocationModal: true, loadingCheckIn: false });
    };

    // trigger the permission prompt once
    const requestPermission = () => {
      navigator.geolocation.getCurrentPosition(
        () => proceed(),
        () => deny()
      );
    };

    if (navigator.permissions?.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then(result => {
          if (result.state === 'granted') {
            proceed();
          } else if (result.state === 'denied') {
            deny();
          } else {
            // state === 'prompt'
            requestPermission();
          }
        })
        .catch(() => requestPermission());
    } else {
      requestPermission();
    }
  } else {
    getPublishedFlowsOnCheckIn();
  }
};

export const Onboarded = ({
  campaign = {},
  checkInObject = {},
  checkOut = mockFunction,
  getPublishedFlowsOnCheckIn = mockFunction,
  handleNavigate = mockFunction,
  hasValidBadge = false,
  isCheckedIn = false,
  isOffline = false,
  selectedCampaign = {},
  transient = {},
  updateApplicationProp = mockFunction,
  updateCampaignProps = mockFunction,
  updateTransientProps = mockFunction,
  user = {}
}) => {
  const { active_badges = [] } = user;
  const { new_published_flow = false, showOnboardedState = false } = transient;

  const userHaveCampaigns = !!campaign[user.id]?.campaigns.length;
  const isOnboarded = userHaveCampaigns;
  const documentsCount = user?.needs_attention_documents?.length;
  const neededForCheckIn = !user.needed_for_check_in?.length && isOnboarded;
  const noPublishedFlows = !campaign[user.id]?.selectedCampaign?.published_flows?.length;
  const isAdminInTestMode = user?.role_name === ADMIN_ROLES.ADMIN && user?.test_mode;
  const disabledCheckIn =
    (!isAdminInTestMode && !hasValidBadge) || isOffline || noPublishedFlows || !!user.needed_for_check_in?.length;
  const selectedCampaignId = campaign[user.id]?.selectedCampaign?.id;
  const getLocationLoading = transient?.loadingCheckIn === selectedCampaignId;

  const loadingStatus = {
    checkIn: transient?.checkInIdLoading === selectedCampaignId,
    checkOut: transient?.checkOutIdLoading === selectedCampaignId
  };

  const getMessage = () => {
    if (isCheckedIn) {
      return REGIONS.UK.includes(user?.country)
        ? I18n.t('campaign:checkInNoPostCode')
        : `${I18n.t('campaign:checkInMessage')} ${checkInObject.selected_postcodes}`;
    }

    if (isOffline) return I18n.t('campaign:checkInOfflineMessage');

    if (hasValidBadge && !noPublishedFlows)
      return `${I18n.t('campaign:cardMessage.notCheckedInMessage')} ${
        new_published_flow ? I18n.t('growl:success.updatedFlow.title') : ''
      }`;

    if (noPublishedFlows && !isOffline) return I18n.t('campaign:cardMessage.noPublishedFlow');

    if (user?.onboarding_profile?.state === 'profile_incomplete') return I18n.t('campaign:cardMessage.noBadgeAssigned');

    if (active_badges.length === 0 && !isOffline && user?.onboarding_profile?.state !== 'profile_in_progress')
      return I18n.t('campaign:allSet');

    return I18n.t('campaign:cardMessage.noBadgeAssigned');
  };

  const renderMessage = () => <div>{getMessage()}</div>;

  return (
    <>
      {!!documentsCount && showOnboardedState && isOnboarded && (
        <>
          <BlockTitle>{I18n.t('campaign:onboarding')}</BlockTitle>
          <div className="px-20">
            <Card description={I18n.t('campaign:cardMessage.neededForCheckIn')} />
            <div className="mt-16">
              <Card
                buttonLabel={I18n.t('campaign:documentsCard.open')}
                buttonPosition={'flex-end'}
                buttonVariant={'transparent'}
                buttonWidth={'auto'}
                className="documents-card"
                color={'red'}
                description={I18n.t('campaign:documentsCard.documentDescription', { count: documentsCount })}
                number={documentsCount}
                onClick={handleNavigate.bind(null, {
                  callback: true,
                  campaign,
                  selectedCampaign,
                  updateApplicationProp,
                  updateCampaignProps,
                  user,
                  value: TABS.DOCUMENTS
                })}
                title={I18n.t('campaign:documentsCard.documents')}
              />
            </div>
          </div>
        </>
      )}

      {((neededForCheckIn && campaign[user.id]?.campaigns?.length) || (!!documentsCount && isOnboarded)) && (
        <>
          <BlockTitle>{I18n.t('campaign:activity')}</BlockTitle>
          <div className="px-20 mb-90">
            <Card
              title={campaign[user?.id]?.selectedCampaign?.name}
              description={renderMessage()}
              onClick={handleCheckIn.bind(null, {
                checkInObject,
                checkOut,
                getPublishedFlowsOnCheckIn,
                isCheckedIn,
                selectedCampaignId,
                updateTransientProps,
                user
              })}
              buttonCardDisabled={disabledCheckIn || loadingStatus.checkIn || getLocationLoading}
              {...(!isCheckedIn && { buttonLabel: I18n.t('campaign:checkIn') })}
              buttonLoading={getLocationLoading || loadingStatus.checkIn}
              iconButtonLeft={<img src={ArrowRightIn} className="pb-2 pr-4" />}
              buttonWidth={'auto'}
            />
            {isCheckedIn && (
              <RowView justifyContent="flex-end" paddingTop={16}>
                <Button.OutlineSmall
                  width={'30%'}
                  disabled={isOffline || loadingStatus.checkOut}
                  processing={transient?.checkOutLoading && loadingStatus.checkOut}
                  onClick={handleCheckIn.bind(null, {
                    checkInObject,
                    checkOut,
                    isCheckedIn,
                    updateTransientProps,
                    user
                  })}>
                  {I18n.t('campaign:checkOut')}
                </Button.OutlineSmall>
              </RowView>
            )}
          </div>
        </>
      )}
    </>
  );
};

Onboarded.propTypes = {
  campaign: PropTypes.object,
  checkInObject: PropTypes.object,
  checkOut: PropTypes.func,
  flowId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  flowReady: PropTypes.bool,
  getPublishedFlowsOnCheckIn: PropTypes.func,
  handleNavigate: PropTypes.func,
  handleVersionUpdateConsent: PropTypes.func,
  handleVersionUpdateModalClose: PropTypes.func,
  hasValidBadge: PropTypes.bool,
  isCheckedIn: PropTypes.bool,
  isOffline: PropTypes.bool,
  selectedCampaign: PropTypes.object,
  transient: PropTypes.object,
  updateApplicationProp: PropTypes.func,
  updateCampaignProps: PropTypes.func,
  updateTransientProps: PropTypes.func,
  user: PropTypes.object
};

export default Onboarded;
